import { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { Ok } from 'ts-results-es';
import { z } from 'zod';

import { PageExceedsLimitError } from '../../../errors/mcpToolError.js';
import { log } from '../../../logging/logger.js';
import { BoundedContext } from '../../../overridableConfig.js';
import { useRestApi } from '../../../restApiInstance.js';
import {
  getWorkbookLineageByLuid,
  getWorkbookLineageQuery,
  mergeWorkbookLineage,
} from '../../../sdks/tableau/methods/lineageUtils.js';
import { Workbook } from '../../../sdks/tableau/types/workbook.js';
import { WebMcpServer } from '../../../server.web.js';
import { getExceptionMessage } from '../../../utils/getExceptionMessage.js';
import { getPage, getPageExceedsLimitMessage, MAX_PAGE_SIZE } from '../../../utils/paginate.js';
import { genericFilterDescription } from '../genericFilterDescription.js';
import { ConstrainedResult, WebTool } from '../tool.js';
import { parseAndValidateWorkbooksFilterString } from './workbooksFilterUtils.js';

const paramsSchema = {
  filter: z.string().optional(),
  pageNumber: z
    .number()
    .int()
    .gt(0)
    .optional()
    .describe('Which 1000-item page to fetch (1-based, default 1).'),
  limit: z
    .number()
    .int()
    .gt(0)
    .max(MAX_PAGE_SIZE)
    .optional()
    .describe(
      'The maximum number of workbooks to return from the requested page (must be <= 1000). Use this to fetch fewer than a full page, e.g. the final partial page a client wants, or `limit: 1` when you only need the `totalAvailable` count and want to minimize the response payload.',
    ),
};

export const getListWorkbooksTool = (server: WebMcpServer): WebTool<typeof paramsSchema> => {
  const listWorkbooksTool = new WebTool({
    server,
    name: 'list-workbooks',
    description: `
  Retrieves a list of workbooks on a Tableau site including their metadata such as name, description, and information about the views contained in the workbook. Supports optional filtering via field:operator:value expressions (e.g., name:eq:Superstore) for precise and flexible workbook discovery.
  To list results based on usage popularity or relevance, use the search-content tool.

  **Supported Filter Fields and Operators**
  | Field             | Operators            |
  |-------------------|----------------------|
  | createdAt         | eq, gt, gte, lt, lte |
  | contentUrl        | eq, in               |
  | displayTabs       | eq                   |
  | favoritesTotal    | eq, gt, gte, lt, lte |
  | hasAlerts         | eq                   |
  | hasExtracts       | eq                   |
  | name              | eq, in               |
  | ownerDomain       | eq, in               |
  | ownerEmail        | eq, in               |
  | ownerName         | eq, in               |
  | projectName       | eq, in               |
  | sheetCount        | eq, gt, gte, lt, lte |
  | size              | eq, gt, gte, lt, lte |
  | subscriptionTotal | eq, gt, gte, lt, lte |
  | tags              | eq, in               |
  | updatedAt         | eq, gt, gte, lt, lte |

  ${genericFilterDescription}

  **Example Usage:**
  - List workbooks with the name "Superstore":
      filter: "name:eq:Superstore"
  - List workbooks in the "Finance" project:
      filter: "projectName:eq:Finance"
  - List workbooks created after January 1, 2023:
      filter: "createdAt:gt:2023-01-01T00:00:00Z"
  - List workbooks with the name "Superstore" in the "Finance" project and created after January 1, 2023:
      filter: "name:eq:Superstore,projectName:eq:Finance,createdAt:gt:2023-01-01T00:00:00Z"
      
  **Pagination**
  This tool returns a single 1000-item page per call. Use \`pageNumber\` to select which 1000-item page to fetch (1-based, default 1).
  The response is a flat object \`{ data, totalAvailable }\`; paginate by incrementing \`pageNumber\` until you have collected \`totalAvailable\` items.
  To get just the count of workbooks matching a request, read \`totalAvailable\` from a single call with \`limit: 1\` — the count is returned regardless of page size, and a small \`limit\` keeps the response tiny.`,
    paramsSchema,
    annotations: {
      title: 'List Workbooks',
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
    callback: async ({ filter, pageNumber, limit }, extra): Promise<CallToolResult> => {
      const configWithOverrides = await extra.getConfigWithOverrides();
      const validatedFilter = filter ? parseAndValidateWorkbooksFilterString(filter) : undefined;
      const maxResultLimit = configWithOverrides.getMaxResultLimit(listWorkbooksTool.name);

      return await listWorkbooksTool.logAndExecute({
        extra,
        args: {},
        callback: async () => {
          const pageExceedsLimitMessage = getPageExceedsLimitMessage({
            pageNumber,
            maxResultLimit,
          });
          if (pageExceedsLimitMessage) {
            return new PageExceedsLimitError(pageExceedsLimitMessage).toErr();
          }

          return new Ok(
            await useRestApi({
              ...extra,
              jwtScopes: listWorkbooksTool.requiredApiScopes,
              callback: async (restApi) => {
                const page = await getPage({
                  pageNumber,
                  limit,
                  maxResultLimit,
                  getDataFn: async ({ pageSize, pageNumber }) => {
                    const { pagination, workbooks: data } =
                      await restApi.workbooksMethods.queryWorkbooksForSite({
                        siteId: restApi.siteId,
                        filter: validatedFilter ?? '',
                        pageSize,
                        pageNumber,
                      });

                    return { pagination, data };
                  },
                });

                const workbooks = page.data;

                if (configWithOverrides.disableMetadataApiRequests || workbooks.length === 0) {
                  return { ...page, data: workbooks };
                }

                try {
                  const response = await restApi.metadataMethods.graphql(
                    getWorkbookLineageQuery(workbooks.map((workbook) => workbook.id)),
                  );
                  const enriched = mergeWorkbookLineage(
                    workbooks,
                    getWorkbookLineageByLuid(response),
                    configWithOverrides.boundedContext.datasourceIds,
                  );
                  return { ...page, data: enriched };
                } catch (error) {
                  log(
                    {
                      message: 'Failed to enrich workbooks with lineage metadata',
                      level: 'warning',
                      logger: 'lineage',
                      data: getExceptionMessage(error),
                    },
                    extra,
                  );
                  return { ...page, data: workbooks };
                }
              },
            }),
          );
        },
        constrainSuccessResult: (page) => {
          const constrained = constrainWorkbooks({
            workbooks: page.data,
            boundedContext: configWithOverrides.boundedContext,
          });

          if (constrained.type !== 'success') {
            return constrained;
          }

          return {
            type: 'success',
            result: {
              data: constrained.result,
              totalAvailable: page.totalAvailable,
            },
          };
        },
      });
    },
  });

  return listWorkbooksTool;
};

export function constrainWorkbooks({
  workbooks,
  boundedContext,
}: {
  workbooks: Array<Workbook>;
  boundedContext: BoundedContext;
}): ConstrainedResult<Array<Workbook>> {
  if (workbooks.length === 0) {
    return {
      type: 'empty',
      message:
        'No workbooks were found. Either none exist or you do not have permission to view them.',
    };
  }

  const { projectIds, workbookIds, tags } = boundedContext;
  if (projectIds) {
    workbooks = workbooks.filter((workbook) =>
      workbook.project?.id ? projectIds.has(workbook.project.id) : false,
    );
  }

  if (workbookIds) {
    workbooks = workbooks.filter((workbook) => workbookIds.has(workbook.id));
  }

  if (tags) {
    workbooks = workbooks.filter((workbook) =>
      workbook.tags?.tag?.some((tag) => tags.has(tag.label)),
    );
  }

  if (workbooks.length === 0) {
    return {
      type: 'empty',
      message: [
        'The set of allowed workbooks that can be queried is limited by the server configuration.',
        'While workbooks were found, they were all filtered out by the server configuration.',
      ].join(' '),
    };
  }

  return {
    type: 'success',
    result: workbooks,
  };
}
